import { createContext, useContext, useState, useCallback, ReactNode } from "react";

// ============ UNIFIED COMMISSION MANAGEMENT ============

export type CommSource = "referral" | "ca_partner" | "affiliate";
export type UnifiedCommStatus = "pending" | "approved" | "paid" | "rejected";
export type UnifiedPayoutStatus = "requested" | "processing" | "completed" | "rejected";

export interface UnifiedCommission {
  id: string;
  source: CommSource;
  sourceId: string;
  userName: string;
  userEmail: string;
  description: string;
  plan?: string;
  amount: number;
  status: UnifiedCommStatus;
  month: string;
  createdAt: string;
  approvedAt?: string;
  paidAt?: string;
}

export interface UnifiedPayout {
  id: string;
  source: CommSource;
  userName: string;
  userEmail: string;
  amount: number;
  status: UnifiedPayoutStatus;
  method: string;
  requestedAt: string;
  approvedAt?: string;
  completedAt?: string;
  transactionRef?: string;
}

export interface PaymentLog {
  id: string;
  payoutId: string;
  action: "requested" | "approved" | "rejected" | "processing" | "completed";
  performedBy: string;
  note?: string;
  timestamp: string;
}

export interface CommissionSummary {
  totalCommissions: number;
  totalPending: number;
  totalApproved: number;
  totalPaid: number;
  totalPayoutRequested: number;
  totalPayoutCompleted: number;
  referralCount: number;
  partnerCount: number;
  affiliateCount: number;
}

// Seed: aggregated from all 3 systems
const SEED_COMMISSIONS: UnifiedCommission[] = [];
const SEED_PAYOUTS: UnifiedPayout[] = [];
const SEED_LOGS: PaymentLog[] = [];

interface CommissionMgmtContextType {
  commissions: UnifiedCommission[];
  payouts: UnifiedPayout[];
  logs: PaymentLog[];
  summary: CommissionSummary;
  approveCommission: (id: string) => void;
  rejectCommission: (id: string) => void;
  approvePayout: (id: string) => void;
  rejectPayout: (id: string) => void;
  completePayout: (id: string, ref: string) => void;
}

const CommissionMgmtContext = createContext<CommissionMgmtContextType | null>(null);

export function CommissionMgmtProvider({ children }: { children: ReactNode }) {
  const [commissions, setCommissions] = useState<UnifiedCommission[]>(SEED_COMMISSIONS);
  const [payouts, setPayouts] = useState<UnifiedPayout[]>(SEED_PAYOUTS);
  const [logs, setLogs] = useState<PaymentLog[]>(SEED_LOGS);

  const addLog = useCallback((payoutId: string, action: PaymentLog["action"], note?: string) => {
    setLogs(prev => [{
      id: `pl-${crypto.randomUUID().slice(0, 8)}`,
      payoutId,
      action,
      performedBy: "Platform Admin",
      note,
      timestamp: new Date().toISOString(),
    }, ...prev]);
  }, []);

  const approveCommission = useCallback((id: string) => {
    setCommissions(prev => prev.map(c => c.id === id && c.status === "pending"
      ? { ...c, status: "approved" as UnifiedCommStatus, approvedAt: new Date().toISOString() } : c));
  }, []);

  const rejectCommission = useCallback((id: string) => {
    setCommissions(prev => prev.map(c => c.id === id && c.status === "pending"
      ? { ...c, status: "rejected" as UnifiedCommStatus } : c));
  }, []);

  const approvePayout = useCallback((id: string) => {
    setPayouts(prev => prev.map(p => p.id === id && p.status === "requested"
      ? { ...p, status: "processing" as UnifiedPayoutStatus, approvedAt: new Date().toISOString() } : p));
    addLog(id, "approved", "Payout approved by admin");
  }, [addLog]);

  const rejectPayout = useCallback((id: string) => {
    setPayouts(prev => prev.map(p => p.id === id && p.status === "requested"
      ? { ...p, status: "rejected" as UnifiedPayoutStatus } : p));
    addLog(id, "rejected", "Payout rejected by admin");
  }, [addLog]);

  const completePayout = useCallback((id: string, ref: string) => {
    setPayouts(prev => prev.map(p => p.id === id && p.status === "processing"
      ? { ...p, status: "completed" as UnifiedPayoutStatus, completedAt: new Date().toISOString(), transactionRef: ref } : p));
    addLog(id, "completed", `Payment completed — ${ref}`);
  }, [addLog]);

  const summary: CommissionSummary = {
    totalCommissions: commissions.reduce((s, c) => s + c.amount, 0),
    totalPending: commissions.filter(c => c.status === "pending").reduce((s, c) => s + c.amount, 0),
    totalApproved: commissions.filter(c => c.status === "approved").reduce((s, c) => s + c.amount, 0),
    totalPaid: commissions.filter(c => c.status === "paid").reduce((s, c) => s + c.amount, 0),
    totalPayoutRequested: payouts.filter(p => p.status === "requested").reduce((s, p) => s + p.amount, 0),
    totalPayoutCompleted: payouts.filter(p => p.status === "completed").reduce((s, p) => s + p.amount, 0),
    referralCount: commissions.filter(c => c.source === "referral").length,
    partnerCount: commissions.filter(c => c.source === "ca_partner").length,
    affiliateCount: commissions.filter(c => c.source === "affiliate").length,
  };

  return (
    <CommissionMgmtContext.Provider value={{
      commissions, payouts, logs, summary,
      approveCommission, rejectCommission, approvePayout, rejectPayout, completePayout,
    }}>
      {children}
    </CommissionMgmtContext.Provider>
  );
}

export function useCommissionMgmt() {
  const ctx = useContext(CommissionMgmtContext);
  if (!ctx) throw new Error("useCommissionMgmt must be used within CommissionMgmtProvider");
  return ctx;
}
